/**
 * One definition of "which talent slots on a booking still need someone".
 *
 * Used by the admin Bookings list (the 📷 🎥 📷² needs badges), the Assign-to-Booking dialog on the
 * talent page (which bookings it offers, and which roles are already taken), and the server route that
 * performs the assignment (refuses to overwrite a real person unless replacement is explicit).
 *
 * Before 4 Sep 2026 each of those had its own copy of the rule and they disagreed: a video package with
 * a placeholder second photographer was "needs a videographer" on the list and invisible in the dialog,
 * and the dialog let a photographer assignment silently replace the one already on the booking.
 */
import { isSecondPhotographerAddon, isSecondVideographerAddon, isVideoAddon } from '../addons/addonFamilies.js'

/** The two "Love & Photos" company profiles that hold a slot until a real talent is assigned. */
export const PLACEHOLDER_TALENT_IDS = Object.freeze([
  '410935f9-6034-40b3-a65f-e9863de60e58', // Love & Photos photographer placeholder
  '7c7191dc-c5e5-43ed-b772-083463a00aa4'  // Love & Photos videographer placeholder
])

// Second videographer is the fourth role (Traci, 28 Sep 2026; column added by migration 20260930120000)
export const ROLES = Object.freeze(['photographer', 'videographer', 'second_photographer', 'second_videographer'])

export const ROLE_COLUMNS = Object.freeze({
  photographer: 'photographer_id',
  videographer: 'videographer_id',
  second_photographer: 'second_photographer_id',
  second_videographer: 'second_videographer_id'
})

export const ROLE_LABELS = Object.freeze({
  photographer: 'Photographer',
  videographer: 'Videographer',
  second_photographer: '2nd Photographer',
  second_videographer: '2nd Videographer'
})

const VIDEO_PACKAGE_TYPES = new Set(['photoVideo', 'photoAndVideo', 'video', 'videoOnly'])

export function isPlaceholderTalent(id) {
  return Boolean(id) && PLACEHOLDER_TALENT_IDS.includes(id)
}

/** The package type, wherever this booking happens to carry it. */
export function bookingPackageType(booking) {
  const p = booking?.personalization_data || {}
  return booking?.package_type
    || p.package_type
    || p.packageType
    || p.package?.type
    || p.package?.packageType
    || ''
}

export function isRoseBooking(booking) {
  const p = booking?.personalization_data || {}
  return booking?.source === 'rose' || p.brand === 'the_rose_photo' || p.rose_booking === true || p.rose_waitlist === true
}

/** Add-on rows as loaded by the admin pages: booking_addons rows with an `addon` object, or bare addon objects. */
function addonRows(booking) {
  return (booking?.add_ons || []).map(row => (row && row.addon !== undefined ? row.addon : row)).filter(Boolean)
}

export function hasSecondShooterAddon(booking) {
  const fromPersonalization = (booking?.personalization_data?.addons || []).some(addon =>
    isSecondPhotographerAddon(addon) || String(addon?.title || '').toLowerCase().includes('second')
  )
  if (fromPersonalization) return true
  // Purchases made after checkout only land in booking_addons; personalization stays as it was
  return addonRows(booking).some(addon => isSecondPhotographerAddon(addon))
}

/** The Second Videographer add-on, at checkout or bought later. */
export function hasSecondVideographerAddon(booking) {
  if ((booking?.personalization_data?.addons || []).some(addon => isSecondVideographerAddon(addon))) return true
  return addonRows(booking).some(addon => isSecondVideographerAddon(addon))
}

export function packageNeedsVideo(booking) {
  if (VIDEO_PACKAGE_TYPES.has(bookingPackageType(booking))) return true
  return addonRows(booking).some(addon => isVideoAddon(addon))
}

export function packageNeedsPhotographer(booking) {
  return bookingPackageType(booking) !== 'videoOnly'
}

/** Who holds a role right now: nobody, a placeholder company profile, or a real talent. */
export function roleOccupant(booking, role) {
  const id = booking?.[ROLE_COLUMNS[role]] || null
  if (!id) return { kind: 'empty', id: null }
  if (isPlaceholderTalent(id)) return { kind: 'placeholder', id }
  return { kind: 'talent', id }
}

function slotOpen(booking, role) {
  return roleOccupant(booking, role).kind !== 'talent'
}

/**
 * Which roles still need a real person. A role is needed when the package (or a purchased add-on)
 * calls for it and the slot is empty or held by a placeholder. Rose bookings are staffed elsewhere.
 */
export function getStaffingNeeds(booking) {
  if (!booking || isRoseBooking(booking)) {
    return { photographer: false, videographer: false, secondPhotographer: false, secondVideographer: false }
  }
  return {
    photographer: packageNeedsPhotographer(booking) && slotOpen(booking, 'photographer'),
    videographer: packageNeedsVideo(booking) && slotOpen(booking, 'videographer'),
    secondPhotographer: hasSecondShooterAddon(booking) && slotOpen(booking, 'second_photographer'),
    secondVideographer: hasSecondVideographerAddon(booking) && slotOpen(booking, 'second_videographer')
  }
}

const MS_DAY = 86400000

/**
 * Coordinator dashboard, "needs assignment": when does a booking with open needs appear?
 *  - Second-photographer and videographer slots are ours to fill (the client never picks them):
 *    they surface as soon as the client's own photographer is settled, pending or confirmed.
 *  - Any open slot on a confirmed / in-progress booking surfaces immediately.
 *  - A pending booking whose primary photographer is still a placeholder waits out the 14 days the
 *    client's chosen talent has to confirm (express checkout), then surfaces.
 *  - A talent cancellation surfaces once the event is within 14 days.
 *  - Past events never surface.
 */
export function shouldSurfaceForCoordinator(booking, needs, now = new Date()) {
  if (!needs || (!needs.photographer && !needs.videographer && !needs.secondPhotographer && !needs.secondVideographer)) return false
  const eventDay = String(booking?.event_date || '').slice(0, 10)
  const today = new Date(now).toISOString().slice(0, 10)
  if (!eventDay || eventDay < today) return false
  const status = booking?.booking_status
  if (status === 'confirmed' || status === 'in_progress') return true
  if (status === 'pending') {
    if ((needs.secondPhotographer || needs.videographer) && !needs.photographer) return true
    if (!booking.created_at) return true
    return new Date(now) - new Date(booking.created_at) > 14 * MS_DAY
  }
  if (status === 'declined_by_talent') {
    const threshold = new Date(new Date(now).getTime() + 14 * MS_DAY).toISOString().slice(0, 10)
    return eventDay <= threshold
  }
  return false
}

export function isFullyStaffed(booking) {
  const n = getStaffingNeeds(booking)
  return !n.photographer && !n.videographer && !n.secondPhotographer
}

/**
 * Should this assignment be refused because the role already has a real person?
 * Returns null when it may proceed, otherwise the refusal (code + who holds the role).
 * Re-assigning the same talent to the role they already hold is allowed (idempotent).
 */
export function assignmentBlockedByOccupant({ booking, role, talentId, replaceExisting = false }) {
  const occupant = roleOccupant(booking, role)
  if (occupant.kind !== 'talent') return null
  if (occupant.id === talentId) return null
  if (replaceExisting) return null
  return { code: 'ROLE_ALREADY_ASSIGNED', role, occupantId: occupant.id }
}
